import { useInscalar } from '@ilg-core/inscalar-web/react'
import { useCallback } from 'react'

import { getFeatureFlagsForEvent } from '@mediaingenuity/totallymoney.cardloanutils'
import Cookies from 'js-cookie'

import {
    EventType,
    InscalarEventBase,
    InscalarEventPageView,
    InscalarEventsCore,
} from '@appTypes/InscalarEvents.interface'

import { useInscalarPreviousStep } from '@hooks/useInscalarPreviousStep'

import { getInscalarAppEnv } from '@utils/getInscalarAppEnv'
import { getPathnameFromAsPath } from '@utils/getPathnameFromAsPath'
import {
    getJourneyCategoryFromJourney,
    getJourneyFromUrl,
    getOrSetOriginalJourney,
    getStepFromUrl,
} from '@utils/inscalar'

import useFeatureFlags from './useFeatureFlags'

export const COOKIE_CONSENT_KEY = 'cookieyes-consent'

const useInscalarTrackEvent = () => {
    const { featureFlags } = useFeatureFlags()
    const { track } = useInscalar()
    const { advance } = useInscalarPreviousStep()

    const trackBase = useCallback(
        (eventType: EventType, data?: InscalarEventBase) => {
            const coreData: InscalarEventsCore = {
                env: getInscalarAppEnv(),
                client: 'web',
                event_type: eventType,
                filters: {
                    cookie_consents: Cookies.get(COOKIE_CONSENT_KEY),
                    experiment_ids:
                        getFeatureFlagsForEvent(featureFlags)?.split(';') ?? [],
                },
            }

            track(
                'frontend_event',
                '8.0',
                {},
                {
                    ...data,
                    // Fixed properties
                    ...coreData,
                }
            )
        },
        [featureFlags, track]
    )

    const trackPageView = useCallback(
        (url: string) => {
            const currentStep = getStepFromUrl(getPathnameFromAsPath(url))

            const currentJourney = getJourneyFromUrl(url)
            const currentJourneyCategory =
                getJourneyCategoryFromJourney(currentJourney)

            const originalJourney = getOrSetOriginalJourney(
                currentJourney,
                currentJourneyCategory
            )
            const previousStep = advance(currentStep)

            trackBase('pageview', {
                journey: currentJourney,
                journey_category: currentJourneyCategory,

                original_journey: originalJourney.journey,
                original_journey_category: originalJourney.journeyCategory,

                step: currentStep,
                previous_step: previousStep,
            })
        },
        [advance, trackBase]
    )

    const trackStep = useCallback(
        ({
            journey,
            journeyCategory,
            originalJourney,
            originalJourneyCategory,
            step,
        }: InscalarEventPageView) => {
            const previousStep = advance(step)

            trackBase('step', {
                journey,
                journey_category: journeyCategory,

                original_journey: originalJourney,
                original_journey_category: originalJourneyCategory,

                step,
                previous_step: previousStep,
            })
        },
        [advance, trackBase]
    )

    const trackAccept = useCallback(
        ({
            step,
            journey,
            journeyCategory,
        }: {
            journey: 'credit_cards' | 'personal_loans'
            journeyCategory: 'credit_cards' | 'loans'
            step: 'credit_cards_scoring' | 'personal_loans_scoring'
        }) => {
            const previousStep = advance(step)

            trackBase('accept', {
                journey,
                journey_category: journeyCategory,

                step,
                previous_step: previousStep,
            })
        },
        [advance, trackBase]
    )

    const trackDecline = useCallback(
        ({
            step,
            journey,
            journeyCategory,
        }: {
            journey: 'credit_cards'
            journeyCategory: 'credit_cards'
            step: 'credit_cards_scoring'
        }) => {
            const previousStep = advance(step)

            trackBase('decline', {
                journey,
                journey_category: journeyCategory,

                step,
                previous_step: previousStep,
            })
        },
        [advance, trackBase]
    )

    return {
        trackPageView,
        trackStep,
        trackAccept,
        trackDecline,
    }
}

export default useInscalarTrackEvent
